import { useMemo } from "react";
import type {
  PrismConfigGroup,
  PrismConfigItem,
  PrismConfigApiItem,
} from "../types/preferences_types";
import { usePrismConfigQuery, useUpdatePrismConfigMutation } from "./usePrismConfigApi";

// 설정 항목의 메타데이터 (라벨, 툴팁, 그룹 등)
const CONFIG_METADATA: Record<
  string,
  { type: "bool" | "int"; label: string; tooltip?: string; min?: number; group: string }
> = {
  COLLECT_BLOCK: {
    type: "bool",
    label: "수집 차단",
    tooltip:
      "데이터 수집 작업을 일시적으로 중단할 때 사용합니다. 활성화하면 모든 새로운 수집 요청이 차단됩니다.",
    group: "수집 관련 설정",
  },
  MAX_CONCURRENT_JOBS: {
    type: "int",
    label: "최대 동시 수집 작업 수",
    tooltip: "동시에 실행되는 collector 애플리케이션 인스턴스 개수를 설정합니다.",
    min: 1,
    group: "수집 관련 설정",
  },
  META_ADS_LIBRARY_MAX_CONCURRENT_JOBS: {
    type: "int",
    label: "Meta 광고 라이브러리 최대 동시 수집 작업 수",
    tooltip:
      "collector 인스턴스당 동시에 실행할 수 있는 meta_ads_library 수집 작업 개수를 설정합니다. 상한을 초과한 작업은 지연 큐로 보내져 잠시 후 재시도됩니다.",
    min: 1,
    group: "수집 관련 설정",
  },
};

// API 응답을 UI 형식으로 변환
function mapApiItemsToGroups(apiItems: PrismConfigApiItem[]): PrismConfigGroup[] {
  const itemsByGroup: Record<string, PrismConfigItem[]> = {};

  apiItems.forEach((apiItem) => {
    // object 타입인 경우, 각 키를 개별 항목으로 펼침
    if (
      typeof apiItem.value === "object" &&
      apiItem.value !== null &&
      !Array.isArray(apiItem.value)
    ) {
      Object.entries(apiItem.value).forEach(([key, val]) => {
        const fullName = `${apiItem.name}.${key}`;
        const metadata = CONFIG_METADATA[fullName];
        if (!metadata) return;

        const groupName = metadata.group;
        if (!itemsByGroup[groupName]) {
          itemsByGroup[groupName] = [];
        }

        if (metadata.type === "bool") {
          itemsByGroup[groupName].push({
            name: fullName,
            type: "bool" as const,
            label: metadata.label,
            tooltip: metadata.tooltip,
            value: Boolean(val),
          });
        } else {
          itemsByGroup[groupName].push({
            name: fullName,
            type: "int" as const,
            label: metadata.label,
            tooltip: metadata.tooltip,
            value: Number(val),
            min: metadata.min,
          });
        }
      });
    } else {
      // 단일 값인 경우
      const metadata = CONFIG_METADATA[apiItem.name];
      if (!metadata) return;

      const groupName = metadata.group;
      if (!itemsByGroup[groupName]) {
        itemsByGroup[groupName] = [];
      }

      if (metadata.type === "bool") {
        itemsByGroup[groupName].push({
          name: apiItem.name,
          type: "bool" as const,
          label: metadata.label,
          tooltip: metadata.tooltip,
          value: Boolean(apiItem.value),
        });
      } else {
        itemsByGroup[groupName].push({
          name: apiItem.name,
          type: "int" as const,
          label: metadata.label,
          tooltip: metadata.tooltip,
          value: Number(apiItem.value),
          min: metadata.min,
        });
      }
    }
  });

  // 그룹별로 정리하여 반환
  return Object.entries(itemsByGroup).map(([groupName, items]) => ({
    title: groupName,
    items,
  }));
}

export function usePrismConfig() {
  const { data: apiItems, isLoading, isError } = usePrismConfigQuery();
  const updateMutation = useUpdatePrismConfigMutation();

  const groups = useMemo(() => {
    if (!apiItems) return [];
    return mapApiItemsToGroups(apiItems);
  }, [apiItems]);

  const changeBool = async (name: string, next: boolean) => {
    try {
      // 중첩된 키인지 확인 (예: "parent.child")
      if (name.includes(".")) {
        const [parentName, childKey] = name.split(".");
        const parentItem = apiItems?.find((item) => item.name === parentName);
        if (parentItem && typeof parentItem.value === "object" && parentItem.value !== null) {
          await updateMutation.mutateAsync({
            name: parentName,
            value: { ...parentItem.value, [childKey]: next },
          });
        }
      } else {
        await updateMutation.mutateAsync({ name, value: next });
      }
    } catch {
      // 에러는 mutation의 onError에서 처리됨
    }
  };

  const saveInt = async (name: string, next: number) => {
    if (!Number.isFinite(next) || next < 1) {
      return;
    }

    try {
      // 중첩된 키인지 확인 (예: "parent.child")
      if (name.includes(".")) {
        const [parentName, childKey] = name.split(".");
        const parentItem = apiItems?.find((item) => item.name === parentName);
        if (parentItem && typeof parentItem.value === "object" && parentItem.value !== null) {
          await updateMutation.mutateAsync({
            name: parentName,
            value: { ...parentItem.value, [childKey]: next },
          });
        }
      } else {
        await updateMutation.mutateAsync({ name, value: next });
      }
    } catch {
      // 에러는 mutation의 onError에서 처리됨
    }
  };

  return {
    groups,
    isLoading,
    isError,
    changeBool,
    saveInt,
  };
}
